import { Component, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BrightnessSettings } from '../../../interfaces/restaurant';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { RestaurantService } from '../../../services/restaurant/restaurant.service';
import { BrightnessScheduleComponent } from '../../modals/brightness-schedule/brightness-schedule.component';
import { MatDialog } from '@angular/material/dialog';

type BrightnessForm = {
  brightnessControl: FormControl<boolean>;
  brightnessLevel: FormControl<number>;
};

@UntilDestroy()
@Component({
  selector: 'app-brightness',
  templateUrl: './brightness.component.html',
  styleUrls: ['./brightness.component.scss']
})
export class BrightnessComponent {
  public brightnessSettings!: BrightnessSettings;
  public brightnessForm: FormGroup<BrightnessForm> = new FormGroup({
    brightnessControl: new FormControl<boolean>(false, { nonNullable: true }),
    brightnessLevel: new FormControl<number>(100, { nonNullable: true })
  });
  public isDisabledForm = false;

  private currentRestaurantId!: number;

  @Input() set restaurantId(value: number | undefined) {
    if (value != null) {
      this.currentRestaurantId = value;
      this.restaurantService.getBrightnessSettings(value)
        .pipe(untilDestroyed(this))
        .subscribe(settings => this.setForm(settings));
    }
  }

  @Input() editable = false;
  @Input() set isSelectedTables(value: boolean) {
    this.isDisabledForm = value;
    this.toggleControls(value);
  }

  constructor(
    private matDialog: MatDialog,
    private restaurantService: RestaurantService
  ) {}

  private setForm(settings: BrightnessSettings): void {
    this.brightnessSettings = settings;

    const controls = {
      brightnessControl: new FormControl<boolean>(
        settings.tablesBrightnessControl ?? false,
        { nonNullable: true, validators: [Validators.min(40), Validators.max(100)] }
      ),
      brightnessLevel: new FormControl<number>(
        Math.round((settings.tablesBrightnessLevel ?? 1) * 100),
        { nonNullable: true }
      )
    };

    this.brightnessForm = new FormGroup(controls);

    this.initFormListeners();
    this.toggleControls(this.isDisabledForm);
  }

  private initFormListeners(): void {
    this.brightnessControl.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(enabled => {
        enabled ? this.brightnessLevelControl.enable({ emitEvent: false })
          : this.brightnessLevelControl.disable({ emitEvent: false });
        this.saveBrightnessSettings({ type: 'control', value: enabled });
      });

    this.brightnessLevelControl.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        debounceTime(300)
      )
      .subscribe(level => {
        if (level != null && this.brightnessLevelControl.valid) {
          const rounded = Math.round(level);
          this.brightnessLevelControl.setValue(rounded, { emitEvent: false });
          this.saveBrightnessSettings({ type: 'level', value: rounded / 100 });
        }
      });
  }

  private toggleControls(disabled: boolean): void {
    if (!this.brightnessForm) {
      return;
    }

    disabled ? this.brightnessControl.disable({ emitEvent: false })
      : this.brightnessControl.enable({ emitEvent: false });

    disabled ? this.brightnessLevelControl.disable({ emitEvent: false })
      : this.brightnessLevelControl.enable({ emitEvent: false });
  }

  get brightnessControl(): FormControl<boolean> {
    return this.brightnessForm.controls.brightnessControl;
  }

  get brightnessLevelControl(): FormControl<number> {
    return this.brightnessForm.controls.brightnessLevel;
  }

  public enforceMaxLength(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.value.length > 3) {
      input.value = input.value.slice(0, 3);
    }

    let numericValue = Number(input.value);
    if (isNaN(numericValue)) {
      numericValue = 100;
    }

    const control = this.brightnessForm.get('brightnessLevel');
    if (control) {
      control.setValue(numericValue, { emitEvent: false });
      control.updateValueAndValidity();
    }
  }

  saveBrightnessSettings(config: { type: 'control' | 'level', value: boolean | number }): void {
    if (config.type === 'control') {
      this.restaurantService.configureBrightnessControl(this.currentRestaurantId, config.value as boolean)
        .pipe(untilDestroyed(this))
        .subscribe();
      this.brightnessSettings.tablesBrightnessControl = config.value as boolean;
    } else {
      this.restaurantService.configureBrightnessLevel(this.currentRestaurantId, config.value as number)
        .pipe(untilDestroyed(this))
        .subscribe();
      this.brightnessSettings.tablesBrightnessLevel = config.value as number;
    }
  }

  brightnessSchedule(): void {
    const dialogRef = this.matDialog.open(BrightnessScheduleComponent, {
      data: {
        restaurantId: this.currentRestaurantId,
        editable: this.editable,
        brightnessSchedule: this.brightnessSettings.tablesBrightnessSchedule
      },
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter(Boolean)
    ).subscribe((result) => {
      const { enabled } = result as { enabled: boolean };
      this.brightnessSettings.tablesBrightnessSchedule = enabled;
    });
  }
}
